import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Paperclip } from 'lucide-react';
import type { Task } from '@shared/types';
import type { BucketWithTasks } from '@/lib/api';
import { DUE_CLASSES, PRIORITY_CLASSES, cn, dueLabel, dueStatus } from '@/lib/format';
import { useMoveTask } from '@/lib/queries';
import { AvatarGroup, LabelChip } from './ui';

/**
 * Kanban with native HTML5 drag and drop.
 *
 * Deliberately no drag library: the interaction is one card into one column,
 * and the native API covers it with zero bundle cost. The tradeoff is no
 * touch support — mobile users get the list view instead, which is the right
 * experience on a phone anyway.
 */
/** Sentinel column id for tasks with no bucket assignment. */
const UNASSIGNED = -1;

export function KanbanBoard({
  viewId,
  buckets,
  unassigned,
}: {
  viewId: number;
  buckets: BucketWithTasks[];
  unassigned: Task[];
}) {
  const moveTask = useMoveTask(viewId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  // The card we'd drop BEFORE within the hovered column (null = append to end).
  const [dropBefore, setDropBefore] = useState<number | null>(null);

  function handleDrop(bucketId: number) {
    const taskId = draggingId;
    const beforeTaskId = dropBefore;
    setDraggingId(null);
    setDropTarget(null);
    setDropBefore(null);
    if (taskId === null) return;

    const from =
      buckets.find((b) => b.tasks.some((t) => t.id === taskId))?.id ??
      (unassigned.some((t) => t.id === taskId) ? UNASSIGNED : null);

    // No-op guard: same column AND dropped onto itself or its current neighbour
    // means nothing changed — skip the mutation so the board doesn't reshuffle.
    if (from === bucketId) {
      const col = bucketId === UNASSIGNED ? unassigned : (buckets.find((b) => b.id === bucketId)?.tasks ?? []);
      const curIdx = col.findIndex((t) => t.id === taskId);
      const beforeIdx = beforeTaskId == null ? col.length : col.findIndex((t) => t.id === beforeTaskId);
      // Dropping right where it already is (before itself, or before the card
      // immediately after it) is a no-op.
      if (beforeTaskId === taskId || beforeIdx === curIdx || beforeIdx === curIdx + 1) return;
    }

    moveTask.mutate({
      taskId,
      bucketId: bucketId === UNASSIGNED ? null : bucketId,
      beforeTaskId: beforeTaskId === taskId ? null : beforeTaskId,
    });
  }

  /**
   * Auto-scroll the board horizontally when a card is dragged near either edge.
   * Native HTML5 drag doesn't scroll the container, so a wide board is otherwise
   * un-draggable to off-screen columns.
   */
  function edgeScroll(clientX: number) {
    const el = scrollRef.current;
    if (!el) return;
    const { left, right } = el.getBoundingClientRect();
    const EDGE = 80;
    if (clientX < left + EDGE) el.scrollLeft -= 14;
    else if (clientX > right - EDGE) el.scrollLeft += 14;
  }

  /**
   * The unassigned column is rendered first and only when non-empty.
   *
   * On this data it holds everything: the Asana migration created columns but
   * never assigned tasks to them, so a board would otherwise look empty while
   * the project clearly has hundreds of tasks. Dragging out of it is how a
   * board actually gets sorted for the first time.
   */
  const columns: Array<BucketWithTasks | { id: number; title: string; tasks: Task[]; limit: null; isDoneBucket: false; hint: string }> =
    unassigned.length > 0
      ? [
          {
            id: UNASSIGNED,
            title: 'Nicht einsortiert',
            tasks: unassigned,
            limit: null,
            isDoneBucket: false as const,
            hint: 'Aus dem Asana-Import ohne Spalte übernommen. Zum Einsortieren in eine Spalte ziehen.',
          },
          ...buckets,
        ]
      : buckets;

  return (
    <div ref={scrollRef} className="scroll-thin flex h-full gap-3 overflow-x-auto p-4">
      {columns.map((bucket) => {
        const overLimit = bucket.limit !== null && bucket.tasks.length > bucket.limit;
        return (
          <section
            key={bucket.id}
            onDragOver={(e) => {
              // Without preventDefault the drop event never fires.
              e.preventDefault();
              setDropTarget(bucket.id);
              edgeScroll(e.clientX);
            }}
            onDragLeave={() => setDropTarget((cur) => (cur === bucket.id ? null : cur))}
            onDrop={() => handleDrop(bucket.id)}
            className={cn(
              'flex max-h-full w-72 shrink-0 flex-col rounded-[var(--radius-card)] surface-sunken',
              'border transition-colors',
              dropTarget === bucket.id ? 'border-brand-400 bg-brand-50/60 dark:bg-brand-900/20' : 'line',
            )}
            aria-label={bucket.title}
          >
            <header className="flex items-center gap-2 px-3 py-2.5">
              <h3
                className={cn(
                  'min-w-0 flex-1 truncate text-[13px] font-semibold',
                  bucket.id === UNASSIGNED ? 'text-muted italic' : 'text-strong',
                )}
                title={'hint' in bucket ? bucket.hint : bucket.title}
              >
                {bucket.title}
              </h3>
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[10px] font-medium tabular-nums',
                  overLimit
                    ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
                    : 'surface-card text-muted',
                )}
                title={bucket.limit !== null ? `Limit: ${bucket.limit}` : undefined}
              >
                {bucket.tasks.length}
                {bucket.limit !== null && `/${bucket.limit}`}
              </span>
            </header>

            <div className="scroll-thin min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2">
              {bucket.tasks.map((task) => (
                <KanbanCard
                  key={task.id}
                  task={task}
                  dragging={draggingId === task.id}
                  showDropLine={dropTarget === bucket.id && dropBefore === task.id}
                  onDragStart={() => setDraggingId(task.id)}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setDropTarget(null);
                    setDropBefore(null);
                  }}
                  onDragOverCard={(topHalf) => {
                    setDropTarget(bucket.id);
                    // Top half → drop before this card; bottom half → before the
                    // next card (i.e. after this one).
                    if (topHalf) {
                      setDropBefore(task.id);
                    } else {
                      const idx = bucket.tasks.findIndex((t) => t.id === task.id);
                      setDropBefore(bucket.tasks[idx + 1]?.id ?? null);
                    }
                  }}
                />
              ))}
              {bucket.tasks.length === 0 && (
                <p className="px-2 py-6 text-center text-xs text-faint">Keine Aufgaben</p>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function KanbanCard({
  task,
  dragging,
  showDropLine,
  onDragStart,
  onDragEnd,
  onDragOverCard,
}: {
  task: Task;
  dragging: boolean;
  showDropLine: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  /** topHalf=true means the cursor is over the card's upper half. */
  onDragOverCard: (topHalf: boolean) => void;
}) {
  const status = dueStatus(task.dueDate, task.done);
  const [coverFailed, setCoverFailed] = useState(false);
  // A cover id can point at an attachment that no longer resolves (the Asana
  // import set cover ids for files that didn't all come across). Without the
  // error handler the reserved 7rem box stays blank and the card looks broken.
  const cover = coverFailed ? null : task.coverImageAttachmentId;

  return (
    <article
      draggable
      // A drop-indicator line above the card when we'd drop before it.
      style={showDropLine ? { boxShadow: '0 -3px 0 -1px var(--color-brand-500)' } : undefined}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation(); // card decides the position, not the column
        const rect = e.currentTarget.getBoundingClientRect();
        onDragOverCard(e.clientY < rect.top + rect.height / 2);
      }}
      onDragStart={(e) => {
        // Required for Firefox to start a drag at all.
        e.dataTransfer.setData('text/plain', String(task.id));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        'cursor-grab rounded-lg surface-card border line p-2.5 shadow-sm transition-all',
        'hover:border-[var(--line-strong)] hover:shadow',
        dragging && 'rotate-1 opacity-40',
        task.done && 'opacity-60',
      )}
    >
      {cover !== null && (
        <img
          src={`/api/tasks/${task.id}/attachments/${cover}`}
          alt=""
          loading="lazy"
          onError={() => setCoverFailed(true)}
          className="mb-2 h-28 w-full rounded object-cover"
        />
      )}

      <Link
        to="/tasks/$taskId"
        params={{ taskId: task.id }}
        className={cn(
          'block text-[13px] leading-snug text-strong hover:text-brand-500',
          task.done && 'line-through',
        )}
      >
        {task.title}
      </Link>

      {task.labels.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {task.labels.slice(0, 3).map((l) => (
            <LabelChip key={l.id} label={l} size="sm" />
          ))}
          {task.labels.length > 3 && (
            <span className="text-[10px] text-faint">+{task.labels.length - 3}</span>
          )}
        </div>
      )}

      {(task.dueDate || task.assignees.length > 0 || task.priority > 0) && (
        <footer className="mt-2 flex items-center gap-2">
          {task.dueDate && (
            <span className={cn('text-[10px]', DUE_CLASSES[status])}>
              {dueLabel(task.dueDate, task.done)}
            </span>
          )}
          {task.priority >= 3 && (
            <span className={cn('text-[10px] font-medium', PRIORITY_CLASSES[task.priority])}>
              !{task.priority}
            </span>
          )}
          {task.attachments && task.attachments.length > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-faint">
              <Paperclip className="size-2.5" aria-hidden />
              {task.attachments.length}
            </span>
          )}
          <span className="ml-auto">
            <AvatarGroup users={task.assignees} max={2} />
          </span>
        </footer>
      )}
    </article>
  );
}
